/**
 * Find Time form field — split-button design:
 *
 *   ┌─────────────────────────────────────────┬─────┐
 *   │  ✨ Find time this week                 │  ▾  │
 *   └─────────────────────────────────────────┴─────┘
 *     main action (applies `this_week`)        menu
 *
 * Clicking the main button directly fills the current week (today
 * → Sunday, weekdays only) — no menu needed for the common case.
 * The small chevron button opens the options list for "Next
 * weekday", "Next N weekdays", "Next week", "Next 2 weeks".
 *
 * Menu items don't carry a persistent "selected" check mark —
 * picking an option fires the apply and closes the menu; the
 * trigger's label doesn't change either. Matches the Figma.
 *
 * All date math anchors on **today**, never the visible calendar
 * week (see `findTime/walker.ts`). Container navigates the calendar
 * to `firstDateKey` on apply so the user lands on the chosen week.
 */
import { useMemo, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Menu, Text, type MenuConfig } from '@kula-ai/components';
import { Stars03, ChevronDown } from '@kula-ai/icons-core';
import {
  FIND_TIME_MODES,
  formatFindTimeLabel,
  formatSlotsSelected,
  walkFindTime,
  type FindTimeValue,
} from '../findTime';

// ─────────────────────────────────────────────────────────────────
// Constants — wording + defaults. Edit here, not in JSX.
// ─────────────────────────────────────────────────────────────────

const MAIN_BUTTON_LABEL = 'Find time this week';
/** The mode the main-button click applies directly. */
const MAIN_BUTTON_MODE: FindTimeValue = 'this_week';

// ─────────────────────────────────────────────────────────────────
// Props.
// ─────────────────────────────────────────────────────────────────

export interface FindTimeApplyArgs {
  value: FindTimeValue;
  targetDateKeys: string[];
  firstDateKey: string | null;
}

export interface FindTimeFieldProps {
  /** Active selection — drives dirty tracking only (no check mark). */
  value: FindTimeValue;
  /** Number of slots currently active across all days. */
  slotsCount: number;
  /** Calendar timezone — drives walker + label formatting. */
  timezone: string;
  /** `true` when the field should be disabled (no interviewer). */
  disabled?: boolean;
  onApply: (_args: FindTimeApplyArgs) => void;
  onClear: () => void;
  onPreview?: () => void;
  minimumNoticeHours?: number;
  /**
   * @deprecated No longer consumed. The previous behaviour filtered
   * Find Time picks to these prefetched days, but the set was
   * scoped to the visible calendar week so navigating shifted
   * every option forward — labels AND picks both drifted as the
   * recruiter scrolled. Find Time now anchors purely on today's
   * weekday progression, matching legacy. Kept on the interface
   * so existing callers compile until the wiring is removed
   * upstream (see `DetailsStep.handleAvailableDaysChange`).
   */
  availableDays?: ReadonlySet<string>;
  /**
   * @deprecated No longer fires — `availableDays` is no longer
   * consumed, so the walker never enters the "we know there's no
   * availability" branch. The calendar itself signals empty
   * picked-days via the rendered slot list.
   */
  onNoAvailability?: (_mode: FindTimeValue) => void;
}

// ─────────────────────────────────────────────────────────────────
// Component.
// ─────────────────────────────────────────────────────────────────

export function FindTimeField({
  value,
  slotsCount,
  timezone,
  disabled = false,
  onApply,
  onClear,
  onPreview,
  minimumNoticeHours,
}: FindTimeFieldProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  // `Date.now()` is impure, so React's compiler forbids calling it
  // during render (including inside `useMemo`). Capture `now` in
  // state at mount and refresh it right when the menu opens, via the
  // Menu's `onOpenChange` — an event callback, not render — so labels
  // reflect a fresh time window each time the user clicks the caret.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  const handleMenuOpenChange = (open: boolean) => {
    if (open) setNowMs(Date.now());
    setMenuOpen(open);
  };

  const items = useMemo<MenuConfig[]>(
    () =>
      FIND_TIME_MODES.flatMap((mode, idx): MenuConfig[] => {
        // Both label AND click intentionally OMIT `availableDays` so
        // the dropdown and the resulting pick stay anchored to today's
        // weekday progression — "Next weekday (Thu, May 14)" always
        // means May 14, regardless of which calendar week is visible.
        //
        // The previous behaviour passed `availableDays` (the pipeline-
        // emitted set of days with at least one bookable slot for the
        // currently-visible / prefetched range). When the user
        // navigated to next week, that set excluded today's adjacent
        // days, the walker silently skipped past them, and every
        // option shifted forward — both the label and the picked
        // dates. The reported business bug ("Find time this week
        // should not change based on the active week") is this drift.
        //
        // Slot-level availability (e.g. interviewer PTO that empties
        // a picked day) is still surfaced by the calendar after the
        // pick lands — the user sees zero slots on that day and can
        // adjust. The `onNoAvailability` callback only fired when
        // `availableDays` was provided AND empty for the pick, so it
        // is no longer reachable from this code path; the dropdown
        // is always actionable.
        const item: MenuConfig = {
          key: mode.value,
          label: formatFindTimeLabel({ mode, nowMs, timezone }),
          // Compact rows + no truncation so the appended date range
          // ("(15 May – 18 May)") always stays visible — matches Figma.
          className: 'py-1.5 px-3 text-sm whitespace-nowrap',
          onClick: () => {
            const { dateKeys, firstDateKey } = walkFindTime({
              mode: mode.value,
              nowMs: Date.now(),
              timezone,
            });
            onApply({
              value: mode.value,
              targetDateKeys: dateKeys,
              firstDateKey,
            });
            setMenuOpen(false);
          },
        };
        // Interleave a separator between rows — Figma shows a thin
        // divider after every option except the last.
        return idx < FIND_TIME_MODES.length - 1
          ? [item, { type: 'separator', key: `${mode.value}-sep` }]
          : [item];
      }),
    [onApply, timezone, nowMs]
  );

  const handleMainClick = () => {
    if (disabled) return;
    const nowMs = Date.now();
    // Match the dropdown items: omit `availableDays` so "Find time
    // this week" always picks the today-anchored week, regardless of
    // which week the calendar happens to be showing. See the dropdown
    // items above for the full rationale.
    const { dateKeys, firstDateKey } = walkFindTime({
      mode: MAIN_BUTTON_MODE,
      nowMs,
      timezone,
    });
    onApply({
      value: MAIN_BUTTON_MODE,
      targetDateKeys: dateKeys,
      firstDateKey,
    });
  };

  const canPreview = slotsCount > 0;
  const canClear = slotsCount > 0 || value !== null;

  return (
    // Card container — all of the Find Time UI (counter,
    // Preview / Clear, split button, minimum-notice note) sits in
    // one light primary-tint panel with rounded corners. No border,
    // per Figma — relies on the surrounding panel's contrast.
    <Box w="100%" p="3" bg="primary-25" borderRadius="md">
      <Flex flexDir="column" gap="1.5">
        {/* ─── Header: counter + Preview + Clear ─── */}
        <Flex align="center" justify="space-between">
          <Text
            variant="p"
            color="heading"
            style={{ fontWeight: 550 }}
            className="text-[14px] leading-[150%]"
          >
            {formatSlotsSelected(slotsCount)}
          </Text>
          <Flex gap="3">
            {onPreview ? (
              <Text
                as="span"
                variant="p"
                color={canPreview ? 'primary' : 'disabled'}
                style={{
                  cursor: canPreview ? 'pointer' : 'not-allowed',
                  fontWeight: 550,
                }}
                className="text-[14px] leading-[20px]"
                role="button"
                tabIndex={canPreview ? 0 : -1}
                onClick={canPreview ? onPreview : undefined}
              >
                Preview
              </Text>
            ) : null}
            <Text
              as="span"
              variant="p"
              color={canClear ? 'primary' : 'disabled'}
              style={{
                cursor: canClear ? 'pointer' : 'not-allowed',
                fontWeight: 550,
              }}
              className="text-[14px] leading-[20px]"
              role="button"
              tabIndex={canClear ? 0 : -1}
              onClick={canClear ? onClear : undefined}
            >
              Clear
            </Text>
          </Flex>
        </Flex>

        {/* ─── Split button: [main label | chevron menu] ─── */}
        {/* Outer wrapper carries the primary-500 outline + radius;
            inner buttons share that border so the seam reads as a
            single divider, per Figma. */}
        <Flex
          w="100%"
          h="32px"
          border="1px solid"
          borderColor={disabled ? 'secondary-100' : 'primary-500'}
          borderRadius="md"
          overflow="hidden"
          opacity={disabled ? 0.7 : 1}
        >
          <Box
            as="button"
            type="button"
            flex="1"
            minW={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="2"
            p="3"
            bg={disabled ? 'secondary-25' : 'white'}
            borderRight="1px solid"
            borderRightColor={disabled ? 'secondary-100' : 'primary-500'}
            color={disabled ? 'secondary-400' : 'primary-500'}
            cursor={disabled ? 'not-allowed' : 'pointer'}
            outline="none"
            _hover={disabled ? undefined : { bg: 'primary-50' }}
            _focus={{ outline: 'none', boxShadow: 'none' }}
            _focusVisible={{ outline: 'none', boxShadow: 'none' }}
            onClick={handleMainClick}
            disabled={disabled}
          >
            <Stars03 size={16} color="currentColor" />
            <Text
              as="span"
              variant="p"
              style={{ fontWeight: 550 }}
              className="text-[14px] leading-[20px] text-primary-500"
            >
              {MAIN_BUTTON_LABEL}
            </Text>
          </Box>
          <Menu
            open={menuOpen}
            onOpenChange={handleMenuOpenChange}
            items={items}
            align="end"
            sideOffset={4}
            triggerAsChild
            // Drop the default `p-1` so the separator lines run edge-to-edge.
            className="!p-0"
            trigger={
              <Box
                as="button"
                type="button"
                w="30px"
                px="1.5"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg={disabled ? 'secondary-25' : 'white'}
                color={disabled ? 'secondary-400' : 'primary-500'}
                cursor={disabled ? 'not-allowed' : 'pointer'}
                outline="none"
                _hover={disabled ? undefined : { bg: 'primary-50' }}
                _focus={{ outline: 'none', boxShadow: 'none' }}
                _focusVisible={{ outline: 'none', boxShadow: 'none' }}
                aria-label="Open Find Time options"
                disabled={disabled}
              >
                <ChevronDown size={16} color="currentColor" />
              </Box>
            }
          />
        </Flex>

        {/* ─── Minimum-notice info note — own white card per Figma. ─── */}
        {minimumNoticeHours && minimumNoticeHours > 0 ? (
          <Box
            bg="white"
            border="1px solid"
            borderColor="secondary-100"
            borderRadius="md"
            p="3"
          >
            <Text
              variant="p"
              color="heading"
              style={{ fontWeight: 550 }}
              className="text-[14px] leading-[150%]"
            >
              <strong>Note:</strong> Candidates can only book interviews
              starting{' '}
              <strong>
                {minimumNoticeHours} hour{minimumNoticeHours > 1 ? 's' : ''}
              </strong>{' '}
              from now. Select slots beyond this timeframe to give candidates
              options.
            </Text>
          </Box>
        ) : null}
      </Flex>
    </Box>
  );
}
